import React, { useEffect, useRef, useState } from "react";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { brandingApi, jobTitlesApi, tradeTypesApi, jobsApi } from "../lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../components/ui/command";
import MessagingIntegrationsCard from "../components/MessagingIntegrationsCard";
import A2PRegistrationCard from "../components/A2PRegistrationCard";
import AppointmentMessagingCard from "../components/AppointmentMessagingCard";
import EmailSettingsCard from "../components/EmailSettingsCard";
import PaymentProcessorsCard from "../components/PaymentProcessorsCard";
import PaymentBehaviorCard from "../components/PaymentBehaviorCard";
import CustomerSignoffCard from "../components/CustomerSignoffCard";
import PermissionTypesCard from "../components/PermissionTypesCard";
import { useAuth } from "../contexts/AuthContext";
import {
  Building2,
  Upload,
  Trash2,
  Loader2,
  Save,
  Image as ImageIcon,
  Briefcase,
  Plus,
  Check,
  X,
  Pencil,
  Palette,
  Users,
  Mail,
  CreditCard,
  Wrench,
  Info,
  ChevronsUpDown,
  Archive,
} from "lucide-react";

/** Company / Branding settings page.
 *  Admins-only (route is wrapped in <RoleRoute roles={['admin']}/> in App.js).
 *  Organized into tabs: Branding · Profile · Team · Email · Payments. */

const TABS = [
  { id: "branding", label: "Branding", icon: Palette },
  { id: "profile", label: "Profile", icon: Building2 },
  { id: "team", label: "Team", icon: Users },
  { id: "tradeTypes", label: "Trade Types", icon: Wrench },
  { id: "jobBoard", label: "Job Pipeline", icon: Archive },
  { id: "email", label: "Email & Messaging", icon: Mail },
  { id: "payments", label: "Payments", icon: CreditCard },
];

export default function CompanySettings() {
  const { hasModule } = useAuth();
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState(() => {
    try {
      return localStorage.getItem("companySettings.tab") || "branding";
    } catch { return "branding"; }
  });
  const fileRef = useRef(null);

  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

  const changeTab = (id) => {
    setTab(id);
    try { localStorage.setItem("companySettings.tab", id); } catch { /* ignore */ }
  };

  const reload = async () => {
    try {
      const b = await brandingApi.get();
      setData(b);
      setDirty(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load company settings");
    }
  };
  useEffect(() => {
    (async () => { await reload(); })();
  }, []);

  // Land on the right tab + surface a toast after the Gmail OAuth redirect round-trip.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    const gmailParam = params.get("gmail");
    if (tabParam) changeTab(tabParam);
    if (gmailParam === "connected") toast.success("Gmail connected");
    if (gmailParam === "error") toast.error("Couldn't connect Gmail. Please try again.");
    if (tabParam || gmailParam) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const mark = (patch) => {
    setData((d) => ({ ...d, ...patch }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { logoDataUrl, ...rest } = data;
      // logoDataUrl is managed exclusively via upload/delete endpoints —
      // never POST it as part of the PUT.
      const updated = await brandingApi.update(rest);
      setData(updated);
      setDirty(false);
      toast.success("Company settings saved");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onPickLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Logo too large (max 2 MB)");
      return;
    }
    setUploading(true);
    try {
      const updated = await brandingApi.uploadLogo(f);
      setData(updated);
      toast.success("Logo uploaded");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the current logo?")) return;
    setUploading(true);
    try {
      const updated = await brandingApi.removeLogo();
      setData(updated);
      toast.success("Logo removed");
    } catch (err) {
      console.error(err);
      toast.error("Remove failed");
    } finally {
      setUploading(false);
    }
  };

  usePageHeader({
    title: "Company Settings",
    subtitle: data ? "Branding & business profile shown on PDFs/emails" : "Branding & business profile",
  });

  if (!data) {
    return (
      <div className="px-6 py-12 text-center text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin inline" />
      </div>
    );
  }

  const logoSrc = data.logoDataUrl
    ? data.logoDataUrl.startsWith("/api/static/")
      ? `${BACKEND}${data.logoDataUrl}`
      : data.logoDataUrl
    : null;

  const showBrandingSave = tab === "branding" || tab === "profile";

  return (
    <>
      <div className="px-6 py-4 max-w-3xl">
        {/* Tab bar */}
        <div
          role="tablist"
          data-testid="company-settings-tabs"
          className="mb-5 -mx-1 flex items-center gap-1 border-b border-slate-200 overflow-x-auto"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                data-testid={`tab-${t.id}`}
                onClick={() => changeTab(t.id)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  active
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="space-y-4" data-testid={`tab-panel-${tab}`}>
          {/* ---------------- Branding tab ---------------- */}
          {tab === "branding" && (
            <>
              {/* Logo card */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="logo-card">
                <div className="flex items-center gap-2 mb-3">
                  <ImageIcon className="h-4 w-4 text-blue-600" />
                  <h2 className="text-[14px] font-semibold text-slate-900">Company logo</h2>
                </div>
                <div className="flex items-center gap-5">
                  <div className="h-24 w-44 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                    {logoSrc ? (
                      <img
                        src={logoSrc}
                        alt="Company logo"
                        data-testid="logo-preview"
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <div className="text-[11px] text-slate-400 uppercase tracking-wider">No logo</div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                      onChange={onPickLogo}
                      data-testid="logo-input"
                      className="hidden"
                    />
                    <Button
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      data-testid="logo-upload-btn"
                      className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-semibold"
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-1.5" />
                      )}
                      Upload logo
                    </Button>
                    {data.logoDataUrl && (
                      <Button
                        variant="outline"
                        onClick={removeLogo}
                        disabled={uploading}
                        data-testid="logo-remove-btn"
                        className="h-9 text-[13px] text-rose-600 hover:bg-rose-50 hover:border-rose-300 border-slate-200 ml-2"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                      </Button>
                    )}
                    <div className="text-[11.5px] text-slate-500">
                      PNG, JPG, WEBP or SVG. Max 2 MB. Used only on your estimates &amp; invoices (PDFs and emails).
                    </div>
                  </div>
                </div>
              </div>

              {/* Accent + footer */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Palette className="h-4 w-4 text-blue-600" />
                  <h2 className="text-[14px] font-semibold text-slate-900">Look &amp; feel</h2>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Accent color">
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={data.accentColor || "#2563EB"}
                        onChange={(e) => mark({ accentColor: e.target.value })}
                        data-testid="settings-accent"
                        className="h-9 w-12 rounded border border-slate-200 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={data.accentColor || ""}
                        onChange={(e) => mark({ accentColor: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                  </Field>
                  <Field label="Footer text on PDFs">
                    <input
                      value={data.footerText || ""}
                      onChange={(e) => mark({ footerText: e.target.value })}
                      data-testid="settings-footer"
                      className={inputClass}
                    />
                  </Field>
                </div>
              </div>

              {/* Terms & Conditions — printed at the bottom of every estimate & invoice PDF */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="h-4 w-4 text-blue-600" />
                  <h2 className="text-[14px] font-semibold text-slate-900">Terms &amp; conditions</h2>
                </div>
                <p className="text-[11.5px] text-slate-500 mb-3">
                  This paragraph is printed on the bottom of every estimate and invoice PDF. Use it for warranties,
                  cancellation, deposit or return policies. Leave blank to hide.
                </p>
                <textarea
                  rows={6}
                  value={data.termsAndConditions || ""}
                  onChange={(e) => mark({ termsAndConditions: e.target.value })}
                  data-testid="settings-terms"
                  placeholder="e.g. All work is guaranteed for 12 months from the date of invoice. Deposits are non-refundable once parts have been ordered. Balances are due upon completion…"
                  className={`${inputClass} min-h-[140px] resize-y leading-relaxed`}
                />
                <div className="mt-1 text-[11px] text-slate-400 text-right tabular-nums">
                  {(data.termsAndConditions || "").length} characters
                </div>
              </div>
            </>
          )}

          {/* ---------------- Profile tab ---------------- */}
          {tab === "profile" && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-4 w-4 text-blue-600" />
                <h2 className="text-[14px] font-semibold text-slate-900">Business profile</h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Company name">
                  <input
                    value={data.companyName || ""}
                    onChange={(e) => mark({ companyName: e.target.value })}
                    data-testid="settings-name"
                    className={inputClass}
                  />
                </Field>
                <Field label="Title">
                  <input
                    value={data.tagline || ""}
                    onChange={(e) => mark({ tagline: e.target.value })}
                    data-testid="settings-tagline"
                    className={inputClass}
                    placeholder="e.g. Owner"
                  />
                </Field>
                <Field label="Address line 1">
                  <input
                    value={data.addressLine1 || ""}
                    onChange={(e) => mark({ addressLine1: e.target.value })}
                    data-testid="settings-addr1"
                    className={inputClass}
                    placeholder="123 Main St"
                  />
                </Field>
                <Field label="Address line 2">
                  <input
                    value={data.addressLine2 || ""}
                    onChange={(e) => mark({ addressLine2: e.target.value })}
                    data-testid="settings-addr2"
                    className={inputClass}
                    placeholder="City, State ZIP"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    value={data.phone || ""}
                    onChange={(e) => mark({ phone: e.target.value })}
                    data-testid="settings-phone"
                    className={inputClass}
                    placeholder="(555) 123-4567"
                  />
                </Field>
                <Field label="Email">
                  <input
                    type="email"
                    value={data.email || ""}
                    onChange={(e) => mark({ email: e.target.value })}
                    data-testid="settings-email"
                    className={inputClass}
                    placeholder="hello@example.com"
                  />
                </Field>
                <div className="col-span-2">
                  <Field label="Time zone">
                    <TimezoneCombobox
                      value={data.timezone || "America/New_York"}
                      onChange={(tz) => mark({ timezone: tz })}
                    />
                  </Field>
                </div>
              </div>
            </div>
          )}

          {/* Branding + Profile share the same Save action */}
          {showBrandingSave && (
            <div className="flex justify-end">
              <Button
                onClick={save}
                disabled={!dirty || saving}
                data-testid="settings-save"
                className="h-10 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[13px] font-semibold"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-1.5" /> Save Settings
                  </>
                )}
              </Button>
            </div>
          )}

          {/* ---------------- Team tab ---------------- */}
          {tab === "team" && (
            <>
              <JobTitlesCard />
              <PermissionTypesCard />
            </>
          )}

          {/* ---------------- Trade Types tab ---------------- */}
          {tab === "tradeTypes" && (
            hasModule("trade_types") ? (
              <TradeTypesCard />
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center" data-testid="trade-types-locked">
                <Wrench className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <div className="text-[14px] font-semibold text-slate-700">Trade Types not enabled</div>
                <div className="text-[12.5px] text-slate-500 mt-1">
                  Ask your account admin to enable the <span className="font-semibold">Trade Types</span> module for your plan.
                </div>
              </div>
            )
          )}

          {/* ---------------- Job Pipeline tab ---------------- */}
          {tab === "jobBoard" && <JobBoardSettingsCard />}

          {/* ---------------- Email & Messaging tab ---------------- */}          {tab === "email" && (
            <>
              <EmailSettingsCard allowGmail={false} />
              <MessagingIntegrationsCard />
              <A2PRegistrationCard />
              <AppointmentMessagingCard />
            </>
          )}

          {/* ---------------- Payments tab ---------------- */}
          {tab === "payments" && (
            hasModule("online_payments") ? (
              <>
                <CustomerSignoffCard />
                <div className="h-4" />
                <PaymentProcessorsCard />
                <PaymentBehaviorCard />
              </>
            ) : (
              <>
                <CustomerSignoffCard />
                <div className="mt-4 bg-white rounded-xl border border-slate-200 p-8 text-center" data-testid="payments-locked">
                  <CreditCard className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <div className="text-[14px] font-semibold text-slate-700">Online payments not enabled</div>
                  <div className="text-[12.5px] text-slate-500 mt-1">
                    Ask your account admin to enable the <span className="font-semibold">Online Payments</span> module for your plan.
                  </div>
                </div>
              </>
            )
          )}
        </div>
      </div>
    </>
  );
}

// ---------- Job titles management ----------
function JobTitlesCard() {
  const [titles, setTitles] = useState(null);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const list = await jobTitlesApi.list();
      setTitles(list);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load job titles");
    }
  };
  useEffect(() => {
    (async () => { await reload(); })();
  }, []);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const created = await jobTitlesApi.create({ name });
      setTitles((t) => [...(t || []), created]);
      setNewName("");
      toast.success(`Added "${created.name}"`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not add title");
    } finally {
      setBusy(false);
    }
  };

  const beginEdit = (t) => {
    setEditingId(t.id);
    setEditingText(t.name);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };
  const saveEdit = async () => {
    const next = editingText.trim();
    if (!next) {
      cancelEdit();
      return;
    }
    setBusy(true);
    try {
      const updated = await jobTitlesApi.update(editingId, { name: next });
      setTitles((arr) => arr.map((x) => (x.id === updated.id ? updated : x)));
      toast.success("Title updated");
      cancelEdit();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Any technicians using this title will be detached.`)) return;
    setBusy(true);
    try {
      await jobTitlesApi.remove(t.id);
      setTitles((arr) => arr.filter((x) => x.id !== t.id));
      toast.success("Title deleted");
    } catch (err) {
      console.error(err);
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="job-titles-card">
      <div className="flex items-center gap-2 mb-3">
        <Briefcase className="h-4 w-4 text-blue-600" />
        <h2 className="text-[14px] font-semibold text-slate-900">Job titles</h2>
        <span className="text-[11px] text-slate-400 ml-1">Selectable on each technician&apos;s Pay tab</span>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="e.g. Lead Technician"
          data-testid="job-title-new"
          className="flex-1 h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
        />
        <Button
          onClick={add}
          disabled={busy || !newName.trim()}
          data-testid="job-title-add"
          className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:bg-slate-200 disabled:text-slate-500 text-white text-[13px] font-semibold"
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      {titles === null ? (
        <div className="text-center py-6 text-slate-400"><Loader2 className="h-4 w-4 inline animate-spin" /></div>
      ) : titles.length === 0 ? (
        <div className="text-[12.5px] text-slate-500 italic py-3">No job titles yet — add the seniority/HR labels you want available on the Pay tab (Apprentice, Journeyman, Lead, Supervisor, …).</div>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
          {titles.map((t) => (
            <li key={t.id} className="px-3 py-2 flex items-center gap-2 bg-white" data-testid={`job-title-row-${t.id}`}>
              {editingId === t.id ? (
                <>
                  <input
                    value={editingText}
                    autoFocus
                    onChange={(e) => setEditingText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" ? saveEdit() : e.key === "Escape" && cancelEdit()}
                    className="flex-1 h-8 px-2 rounded-md border border-blue-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  <button onClick={saveEdit} disabled={busy} className="h-7 px-2 rounded text-[11.5px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center">
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={cancelEdit} className="h-7 px-2 rounded text-[11.5px] font-semibold border border-slate-200 hover:bg-slate-50 flex items-center">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </>
              ) : (
                <>
                  <span className="text-[10.5px] font-bold text-slate-400 w-6">#{t.order}</span>
                  <span className="flex-1 text-[13px] font-semibold text-slate-900">{t.name}</span>
                  <span className="text-[10.5px] font-mono text-slate-400">{t.id}</span>
                  <button
                    onClick={() => beginEdit(t)}
                    title="Rename"
                    className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => remove(t)}
                    disabled={busy}
                    data-testid={`job-title-delete-${t.id}`}
                    title="Delete"
                    className="h-7 w-7 rounded hover:bg-rose-50 flex items-center justify-center text-slate-500 hover:text-rose-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function JobBoardSettingsCard() {
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    jobsApi.boardSettings()
      .then((d) => setDays(d.autoArchiveDelayDays ?? 7))
      .catch(() => toast.error("Could not load Job Pipeline settings"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await jobsApi.updateBoardSettings({ autoArchiveDelayDays: Number(days) || 0 });
      setDays(res.autoArchiveDelayDays);
      setDirty(false);
      toast.success("Job Pipeline settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-[13px] text-slate-500 py-6 text-center">Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid="job-board-settings-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[15px] font-bold text-slate-900">Job Pipeline — Auto-Archive</h3>
          <p className="text-[12px] text-slate-500 mt-0.5">
            Automatically move Completed–Paid and Canceled jobs off the active board after a delay.
          </p>
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="job-board-settings-save-btn"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="rounded-xl border p-4 flex items-start gap-3" style={{ backgroundColor: "#EBF5FF", borderColor: "#BFDBFE" }}>
        <Info className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#2563EB" }} />
        <div className="text-[12.5px] leading-relaxed" style={{ color: "#1E3A8A" }}>
          Jobs are never deleted — archiving only removes them from the active Job Pipeline board (they stay searchable via "Archived Jobs" / "All Jobs" board views). A job is
          skipped automatically if it has a balance due, an unsent invoice, a missing completion signature, a pending/failed payment, a required follow-up, an open return
          visit, or an "Archive Hold" set from its card menu.
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 max-w-sm">
        <label className="text-[12.5px] font-semibold text-slate-700 block mb-1.5">Auto-archive delay (days)</label>
        <input
          type="number"
          min={0}
          value={days}
          onChange={(e) => { setDays(e.target.value); setDirty(true); }}
          data-testid="job-board-settings-delay-input"
          className="h-9 w-full px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:border-blue-400"
        />
        <p className="text-[11.5px] text-slate-400 mt-1.5">Default is 7 days. Applies company-wide to both Completed–Paid and Canceled jobs.</p>
      </div>
    </div>
  );
}

function TradeTypesCard() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    tradeTypesApi
      .get()
      .then((d) => setTrades(d.trades || []))
      .catch(() => toast.error("Could not load trade types"))
      .finally(() => setLoading(false));
  }, []);

  const mutate = (fn) => {
    setTrades((prev) => fn(prev));
    setDirty(true);
  };

  const addTrade = () => mutate((prev) => [...prev, { name: "New Trade", isDefault: false, subCategories: [] }]);
  const renameTrade = (idx, name) => mutate((prev) => prev.map((t, i) => (i === idx ? { ...t, name } : t)));
  const removeTrade = (idx) => mutate((prev) => prev.filter((_, i) => i !== idx));
  const addSubCategory = (tIdx) =>
    mutate((prev) => prev.map((t, i) => (i === tIdx ? { ...t, subCategories: [...t.subCategories, "New Sub-category"] } : t)));
  const renameSubCategory = (tIdx, sIdx, name) =>
    mutate((prev) =>
      prev.map((t, i) => (i === tIdx ? { ...t, subCategories: t.subCategories.map((s, j) => (j === sIdx ? name : s)) } : t))
    );
  const removeSubCategory = (tIdx, sIdx) =>
    mutate((prev) => prev.map((t, i) => (i === tIdx ? { ...t, subCategories: t.subCategories.filter((_, j) => j !== sIdx) } : t)));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = trades
        .map((t) => ({ ...t, name: (t.name || "").trim(), subCategories: (t.subCategories || []).map((s) => s.trim()).filter(Boolean) }))
        .filter((t) => t.name);
      const res = await tradeTypesApi.update(cleaned);
      setTrades(res.trades || cleaned);
      setDirty(false);
      toast.success("Trade types saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save trade types");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-[13px] text-slate-500 py-6 text-center">Loading trade types…</div>;
  }

  return (
    <div className="space-y-4" data-testid="trade-types-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[15px] font-bold text-slate-900">Trade Types</h3>
          <p className="text-[12px] text-slate-500 mt-0.5">
            Manage your trades and sub-categories — used for job creation and staff qualifications.
          </p>
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="trade-types-save-btn"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="rounded-xl border p-4 flex items-start gap-3" style={{ backgroundColor: "#EBF5FF", borderColor: "#BFDBFE" }} data-testid="trade-types-instructions">
        <Info className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#2563EB" }} />
        <div className="text-[12.5px] leading-relaxed" style={{ color: "#1E3A8A" }}>
          <div className="font-bold text-[13.5px] mb-1.5" style={{ color: "#1E3A8A" }}>Setting Up Your Trade Types</div>
          <p className="mb-2">Trade Types define the services your company offers and control which technicians can be assigned to each job type.</p>
          <p className="mb-2">Trades are your top-level service categories (e.g. HVAC, Plumbing, Electrical). Add as many as your business needs using the "+ Add Trade" button.</p>
          <p className="mb-2">Sub-categories define the specific type of work within each trade (e.g. Service Call, Installation, Maintenance). Each trade has its own independent sub-categories. Click "+ Add Sub-category" under any trade to add one.</p>
          <p className="mb-2"><span className="font-semibold">Assigning qualifications to technicians:</span> Once your trades and sub-categories are set up here, go to Staff → select a technician → Profile tab → Qualifications section. Activate the trades and check the sub-categories that technician is approved to handle. A technician will only appear as an eligible assignee for a job if their qualifications match both the job's trade and sub-category.</p>
          <p className="mb-2"><span className="font-semibold">Overriding a qualification:</span> When assigning a job, technicians who do not meet the trade, sub-category, or schedule requirements will appear greyed out. You can still assign them by clicking their name, reviewing the reason they are disqualified, and typing "Override" in the confirmation field.</p>
          <p><span className="font-semibold">Assigning a technician to a job:</span> Open any job from the Jobs page or Dispatch Board, tap or click "Assign Technician," and select from the list of qualified available technicians.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {trades.map((t, tIdx) => (
          <div key={tIdx} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`trade-type-card-${tIdx}`}>
            <div className="flex items-center gap-2 mb-3">
              <Wrench className="h-4 w-4 text-blue-600 shrink-0" />
              <input
                value={t.name}
                onChange={(e) => renameTrade(tIdx, e.target.value)}
                data-testid={`trade-type-name-${tIdx}`}
                className="flex-1 h-8 px-2 rounded-md border border-transparent hover:border-slate-200 focus:border-blue-400 text-[14px] font-bold text-slate-900 focus:outline-none transition-colors"
              />
              {t.isDefault && (
                <span className="text-[10px] font-semibold uppercase text-slate-400 shrink-0">Default</span>
              )}
              <button
                onClick={() => removeTrade(tIdx)}
                disabled={t.isDefault}
                title={t.isDefault ? "Default trades can't be deleted" : "Delete trade"}
                data-testid={`trade-type-delete-${tIdx}`}
                className="h-7 w-7 rounded hover:bg-rose-50 flex items-center justify-center text-slate-400 hover:text-rose-600 disabled:opacity-30 disabled:hover:bg-transparent shrink-0"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="space-y-1.5">
              {(t.subCategories || []).map((s, sIdx) => (
                <div key={sIdx} className="flex items-center gap-1.5" data-testid={`trade-type-subcat-row-${tIdx}-${sIdx}`}>
                  <input
                    value={s}
                    onChange={(e) => renameSubCategory(tIdx, sIdx, e.target.value)}
                    data-testid={`trade-type-subcat-name-${tIdx}-${sIdx}`}
                    className="flex-1 h-8 px-2 rounded-md border border-transparent hover:border-slate-200 focus:border-blue-400 text-[12.5px] text-slate-700 focus:outline-none transition-colors"
                  />
                  <button
                    onClick={() => removeSubCategory(tIdx, sIdx)}
                    data-testid={`trade-type-subcat-delete-${tIdx}-${sIdx}`}
                    className="h-7 w-7 rounded hover:bg-rose-50 flex items-center justify-center text-slate-400 hover:text-rose-600 shrink-0"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={() => addSubCategory(tIdx)}
              data-testid={`trade-type-add-subcat-${tIdx}`}
              className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-blue-600 hover:text-blue-700"
            >
              <Plus className="h-3.5 w-3.5" /> Add Sub-category
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={addTrade}
        data-testid="trade-type-add-btn"
        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg border border-slate-200 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
      >
        <Plus className="h-4 w-4" /> Add Trade
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

// All standard IANA time zones — used by the Time Zone searchable dropdown below.
// `Intl.supportedValuesOf` covers every modern browser; a small common-zone
// fallback keeps the field usable on the rare browser that lacks it.
const IANA_TIMEZONES = (() => {
  try {
    const zones = Intl.supportedValuesOf("timeZone");
    if (Array.isArray(zones) && zones.length) return zones;
  } catch { /* fall through to fallback list below */ }
  return [
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "America/Anchorage", "Pacific/Honolulu", "America/Phoenix", "America/Sao_Paulo",
    "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Moscow",
    "Asia/Dubai", "Asia/Kolkata", "Asia/Shanghai", "Asia/Tokyo", "Asia/Singapore",
    "Australia/Sydney", "Pacific/Auckland", "UTC",
  ];
})();

function TimezoneCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid="settings-timezone-trigger"
          className={`${inputClass} flex items-center justify-between text-left`}
        >
          <span className="truncate">{value || "Select time zone…"}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popper-anchor-width] p-0" align="start" data-testid="settings-timezone-popover">
        <Command filter={(value, search) => {
          const norm = (s) => s.toLowerCase().replace(/[_/]/g, " ");
          return norm(value).includes(norm(search)) ? 1 : 0;
        }}>
          <CommandInput placeholder="Search time zones…" data-testid="settings-timezone-search" />
          <CommandList>
            <CommandEmpty>No time zone found.</CommandEmpty>
            <CommandGroup>
              {IANA_TIMEZONES.map((tz) => (
                <CommandItem
                  key={tz}
                  value={tz}
                  data-testid={`settings-timezone-option-${tz}`}
                  onSelect={() => { onChange(tz); setOpen(false); }}
                >
                  <Check className={`h-3.5 w-3.5 mr-2 ${value === tz ? "opacity-100" : "opacity-0"}`} />
                  {tz}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const inputClass =
  "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors";
